import React, { useState, useEffect } from 'react';
import { ugcAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { FileImage, Eye, CheckCircle, XCircle, Clock } from 'lucide-react';

export default function AdminUGC() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter]);

  const load = async () => {
    try {
      const res = await ugcAPI.list({ status: filter !== 'all' ? filter : undefined });
      setSubmissions(res.data.submissions || []);
    } catch { toast.error('Failed to load submissions'); }
    finally { setLoading(false); }
  };

  const review = async (id, status) => {
    try {
      await ugcAPI.review(id, { status });
      toast.success(`Submission ${status}`);
      load();
    } catch { toast.error('Failed to review submission'); }
  };

  const statusColor = (s) => {
    const map = { pending: 'bg-yellow-100 text-yellow-700', approved: 'bg-green-100 text-green-700', rejected: 'bg-red-100 text-red-700', reviewed: 'bg-blue-100 text-blue-700' };
    return map[s] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#1A1A1A]">User-Generated Content</h1>
        <p className="text-sm text-[#5B616E] mt-1">Review and moderate reader submissions, tips, and contributed content</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        {[
          { label: 'Total Submissions', value: submissions.length, color: '#1E5A8A' },
          { label: 'Pending Review', value: submissions.filter(s => s.status === 'pending').length, color: '#B89B5E' },
          { label: 'Approved', value: submissions.filter(s => s.status === 'approved').length, color: '#1F7A5A' },
          { label: 'Rejected', value: submissions.filter(s => s.status === 'rejected').length, color: '#B3261E' },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-xl border border-[#E5DFD5] p-5">
            <p className="text-xs text-[#5B616E] font-medium mb-1">{s.label}</p>
            <p className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</p>
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        {['all', 'pending', 'approved', 'rejected'].map(f => (
          <button key={f} onClick={() => { setFilter(f); setLoading(true); }} className={`px-4 py-2 rounded-lg text-sm font-medium capitalize ${filter === f ? 'bg-[#1E5A8A] text-white' : 'bg-white border border-[#E5DFD5] text-[#5B616E] hover:bg-[#F5F0E8]'}`}>
            {f}
          </button>
        ))}
      </div>

      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-8 text-[#5B616E]">Loading submissions...</div>
        ) : submissions.length === 0 ? (
          <div className="text-center py-8 text-[#5B616E]">No submissions found.</div>
        ) : submissions.map(s => (
          <div key={s.id} className="bg-white rounded-xl border border-[#E5DFD5] p-5">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#1E5A8A]/10 flex items-center justify-center">
                  <FileImage className="w-5 h-5 text-[#1E5A8A]" />
                </div>
                <div>
                  <p className="font-medium text-[#1A1A1A]">{s.title || 'Untitled submission'}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-[#5B616E] capitalize">Type: {s.type}</span>
                    <Badge className={statusColor(s.status)}>{s.status}</Badge>
                    {s.is_anonymous && <Badge variant="outline" className="text-[10px]">Anonymous</Badge>}
                  </div>
                </div>
              </div>
              <span className="text-xs text-[#5B616E]">{s.created_at ? new Date(s.created_at).toLocaleDateString() : ''}</span>
            </div>
            <p className="text-sm text-[#5B616E] mb-3">{s.content}</p>
            {s.media_urls?.length > 0 && (
              <div className="flex gap-2 mb-3">
                {s.media_urls.map((url, i) => (
                  <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#1E5A8A] hover:underline flex items-center gap-1">
                    <Eye className="w-3 h-3" /> Media {i + 1}
                  </a>
                ))}
              </div>
            )}
            {s.status === 'pending' && (
              <div className="flex gap-2 pt-3 border-t border-[#E5DFD5]">
                <Button size="sm" onClick={() => review(s.id, 'approved')} className="bg-[#1F7A5A] hover:bg-[#1F7A5A]/90">
                  <CheckCircle className="w-3 h-3 mr-1" /> Approve
                </Button>
                <Button size="sm" variant="outline" onClick={() => review(s.id, 'rejected')} className="text-[#B3261E] border-[#B3261E] hover:bg-red-50">
                  <XCircle className="w-3 h-3 mr-1" /> Reject
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
